import { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import {
	AkselLocale,
	DatePicker as Aksel,
	useDatepicker,
} from 'components/form/aksel';

export function DateInput({
	label,
	value,
	onChange,
	onInvalidDate,
	error,
	required,
	disabled,
	size,
	hideLabel,
}) {
	/* Suppress the date picker callback when synchronizing the form value. */
	const isSyncing = useRef(false);
	const hasValidValue = value && dayjs(value).isValid();

	const { datepickerProps, inputProps, selectedDay, setSelected } =
		useDatepicker({
			defaultSelected: hasValidValue ? new Date(value) : undefined,
			required,
			onValidate: validation => {
				if (isSyncing.current) return;
				if (validation.isEmpty) onChange(null);
				else if (!validation.isValidDate) onInvalidDate?.();
			},
			onDateChange: date => {
				if (!date || isSyncing.current) return;

				// Date-only values use local noon.
				const newDate = dayjs(date)
					.set('hour', 12)
					.set('minute', 0)
					.set('second', 0)
					.toDate();

				onChange(newDate);
			},
		});

	const held = value ? new Date(value).toDateString() : '';
	const shown = selectedDay ? selectedDay.toDateString() : '';

	useEffect(() => {
		// Only external day changes synchronize the picker. Local drafts may
		// have no selected day, and must not restore the previous form value.
		if (held === shown || (value && !hasValidValue)) return;

		isSyncing.current = true;
		setSelected(held === '' ? undefined : new Date(held));
		isSyncing.current = false;
	}, [held]);

	return (
		<AkselLocale>
			<Aksel
				{...datepickerProps}
				disabled={disabled ? [true] : undefined}
			>
				<Aksel.Input
					{...inputProps}
					label={label}
					disabled={disabled}
					size={size}
					hideLabel={hideLabel}
					{...(error?.message === undefined
						? {}
						: { error: error.message })}
				/>
			</Aksel>
		</AkselLocale>
	);
}

export default function DatePicker({
	name,
	label,
	defaultValue = new Date(),
	disabled = false,
	required = false,
	rules = {},
}) {
	const { control } = useFormContext();

	const requiredInRules = required
		? { required: 'Dette feltet er påkrevd' }
		: {};

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={defaultValue}
			rules={{
				...requiredInRules,
				...rules,
				validate: {
					...(typeof rules.validate === 'function'
						? { custom: rules.validate }
						: rules.validate),
					validDate: (value, values) =>
						value && !dayjs(value).isValid()
							? 'Skriv inn en gyldig dato'
							: (rules.validate?.validDate?.(value, values) ??
								true),
				},
			}}
			render={({
				field: { onChange, value, disabled: fieldDisabled },
				fieldState: { error },
			}) => (
				<DateInput
					label={label}
					value={value}
					onChange={onChange}
					// Invalidate the form value without replacing the visible draft.
					// Date-schema resolvers also reject this instead of the old date.
					onInvalidDate={() => onChange(new Date(NaN))}
					error={error}
					required={required}
					disabled={fieldDisabled || disabled}
				/>
			)}
		/>
	);
}
